// ============================================
// ACCENT COLOR ENGINE
// The owner picks one color in Settings → Appearance. This works out a
// matching CSS "H S% L%" triplet (the format Tailwind's hsl(var(--primary))
// setup expects) plus a readable foreground, and writes them onto the page
// as CSS variables so the whole app reacts to a single color, instantly.
// ============================================

export const DEFAULT_ACCENT_COLOR = "#EA580C"; // matches the orange baked into index.css

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : clean;
  const bigint = parseInt(full, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

export function isValidHexColor(hex) {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

function hslString({ h, s, l }) {
  return `${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%`;
}

export function hexToHslTriplet(hex) {
  const { r, g, b } = hexToRgb(hex);
  return hslString(rgbToHsl(r, g, b));
}

// Blends toward black — used for the "strong" shade every hover/darker
// state (orange-700 etc.) across the app maps onto.
function darkenTriplet(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  return hslString(rgbToHsl(r * (1 - amount), g * (1 - amount), b * (1 - amount)));
}

// A light accent needs dark text on it; a deep one needs white.
function idealForeground(hex) {
  const { r, g, b } = hexToRgb(hex);
  const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return brightness < 128 ? "0 0% 100%" : "0 0% 9%";
}

// Writes the accent onto :root as CSS variables. Every existing
// hsl(var(--primary)) / hsl(var(--ring)) / hsl(var(--accent)) usage across
// the app (buttons, focus rings, active nav, links) picks this up
// immediately — no reload needed. Tailwind's `orange` palette itself is
// mapped onto --primary/--primary-strong (see tailwind.config.js), so every
// existing orange-500/600/700 className in the app re-themes too, with
// nothing else to touch.
export function applyAccentColor(hex) {
  if (!isValidHexColor(hex)) return;
  const triplet = hexToHslTriplet(hex);
  const strongTriplet = darkenTriplet(hex, 0.18);
  const foreground = idealForeground(hex);
  const root = document.documentElement;

  root.style.setProperty("--primary", triplet);
  root.style.setProperty("--primary-strong", strongTriplet);
  root.style.setProperty("--primary-foreground", foreground);
  root.style.setProperty("--ring", triplet);
  root.style.setProperty("--accent", triplet);
  root.style.setProperty("--accent-foreground", foreground);
  root.style.setProperty("--accent-hex", hex);
}

export const ACCENT_PRESETS = [
  { key: "sunset-orange", name: "Sunset Orange", hex: "#F97316" },
  { key: "electric-blue", name: "Electric Blue", hex: "#2563EB" },
  { key: "emerald", name: "Emerald", hex: "#059669" },
  { key: "royal-violet", name: "Royal Violet", hex: "#7C3AED" },
  { key: "rose", name: "Rose", hex: "#E11D48" },
  { key: "amber", name: "Amber", hex: "#D97706" },
];
